import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { resendVerificationEmail } from '@services/emailVerification';
import {
	dismissVerificationBanner,
	shouldShowVerificationBanner,
} from '@utils/emailVerificationBanner';
import { useI18n } from '../i18n';

export default function EmailVerificationBanner() {
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;

	const [visible, setVisible] = useState(false);
	const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
	const [resendMessage, setResendMessage] = useState<string | null>(null);

	useEffect(() => {
		if (!user?.id) {
			setVisible(false);
			return;
		}
		setVisible(shouldShowVerificationBanner(user.id, user.emailVerified));
	}, [user?.id, user?.emailVerified]);

	const verificationDeadlineLabel = useMemo(() => {
		if (!user?.verificationGraceExpiresAt) return null;
		const date = new Date(user.verificationGraceExpiresAt);
		if (Number.isNaN(date.getTime())) return null;
		return date.toLocaleDateString(
			language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-US',
			{ year: 'numeric', month: 'short', day: 'numeric' }
		);
	}, [user?.verificationGraceExpiresAt, language]);

	if (!user || !visible) {
		return null;
	}

	const handleDismiss = () => {
		dismissVerificationBanner(user.id);
		setVisible(false);
	};

	const handleResend = async () => {
		if (resendState === 'sending') return;
		setResendState('sending');
		setResendMessage(null);
		try {
			const result = await resendVerificationEmail(user.id);
			setResendState('sent');
			setResendMessage(
				result.message ||
					copy(
						'Verification email sent. Check your inbox.',
						'Bestätigungs-E-Mail gesendet. Prüfen Sie Ihren Posteingang.',
						'Potvrda e-pošte poslana. Provjerite pretinac.'
					)
			);
		} catch (err: unknown) {
			setResendState('error');
			const message = err instanceof Error ? err.message : null;
			setResendMessage(
				message ||
					copy(
						'Could not send verification email.',
						'Bestätigungs-E-Mail konnte nicht gesendet werden.',
						'Nije moguće poslati potvrdu e-pošte.'
					)
			);
		}
	};

	const alertClass = user.isRestricted ? 'alert-danger' : 'alert-warning';

	return (
		<div className="border-bottom">
			<div className="container-fluid px-2 px-sm-3 py-2">
				<div className={`alert ${alertClass} mb-0 d-flex align-items-start gap-3`} role="alert">
					<i
						className={`bi ${user.isRestricted ? 'bi-exclamation-octagon-fill' : 'bi-envelope-exclamation-fill'} mt-1 flex-shrink-0`}
					></i>
					<div className="flex-grow-1 min-w-0">
						<div className="fw-semibold">
							{user.isRestricted
								? copy(
										'Your email is not verified — account is read-only',
										'Ihre E-Mail ist nicht bestätigt — Konto ist schreibgeschützt',
										'E-pošta nije potvrđena — račun je samo za čitanje'
									)
								: copy(
										'Please verify your email',
										'Bitte bestätigen Sie Ihre E-Mail',
										'Molimo potvrdite e-poštu'
									)}
						</div>
						<div className="small mb-2">
							{user.isRestricted
								? copy(
										'Verify your email to restore full access. We sent a link when you signed up.',
										'Bestätigen Sie Ihre E-Mail, um vollen Zugriff wiederherzustellen. Beim Registrieren wurde ein Link gesendet.',
										'Potvrdite e-poštu za puni pristup. Link smo poslali pri registraciji.'
									)
								: verificationDeadlineLabel
									? copy(
											`We sent a verification link to ${user.email}. Confirm by ${verificationDeadlineLabel} to keep full access.`,
											`Wir haben einen Bestätigungslink an ${user.email} gesendet. Bestätigen Sie bis ${verificationDeadlineLabel}, um vollen Zugriff zu behalten.`,
											`Poslali smo link za potvrdu na ${user.email}. Potvrdite do ${verificationDeadlineLabel} kako biste zadržali puni pristup.`
										)
									: copy(
											`We sent a verification link to ${user.email}. Confirm it to secure your account and keep full access.`,
											`Wir haben einen Bestätigungslink an ${user.email} gesendet. Bestätigen Sie ihn, um Ihr Konto zu sichern und vollen Zugriff zu behalten.`,
											`Poslali smo link za potvrdu na ${user.email}. Potvrdite ga kako biste osigurali račun i zadržali puni pristup.`
										)}
						</div>
						<div className="d-flex flex-wrap align-items-center gap-2">
							<button
								type="button"
								className="btn btn-sm btn-success"
								onClick={handleResend}
								disabled={resendState === 'sending'}
							>
								{resendState === 'sending'
									? copy('Sending...', 'Senden...', 'Slanje...')
									: copy('Resend verification email', 'Bestätigungs-E-Mail erneut senden', 'Ponovno pošalji potvrdu')}
							</button>
							<Link
								to="/app/profile"
								className="btn btn-sm btn-outline-success"
							>
								{copy('Go to profile', 'Zum Profil', 'Idi na profil')}
							</Link>
							{resendMessage && (
								<span
									className={`small ${resendState === 'error' ? 'text-danger' : 'text-success'}`}
								>
									{resendMessage}
								</span>
							)}
						</div>
					</div>
					<button
						type="button"
						className="btn-close flex-shrink-0"
						aria-label={copy('Dismiss', 'Schließen', 'Zatvori')}
						onClick={handleDismiss}
					></button>
				</div>
			</div>
		</div>
	);
}
